import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@material-ui/core";
import { Button } from "@material-ui/core";
import { GoogleMapsProvider } from "pages/Tracking/GoogleMapsManager";

const EMPTY_FUNC = () => {};
const DialogContext = React.createContext([EMPTY_FUNC, EMPTY_FUNC]);
export const useDialog = () => React.useContext(DialogContext);

function DialogContainer(props) {
  const { 
    content, 
    Content, 
    title, 
    actions, 
    open, 
    onClose, 
    onKill, 
    width, 
    disableBackdropClick,
    dialogId // Add unique identifier
  } = props;
  const [data, setData] = useState();
  
  const handleClose = (event, reason) => {
    // Prevent closing on backdrop click if disabled
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    // Call the close handler with the dialog ID
    onClose(dialogId);
  };
  
  return (
    <Dialog 
      fullWidth={width ? false : true} 
      maxWidth={width} 
      open={open} 
      onClose={handleClose} 
      onExited={() => onKill(dialogId)}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <GoogleMapsProvider>
          {typeof content === "string" && <div>{content}</div>}
          {/* {Content && <Content {...props} />} */}
					{Content && <Content {...props} onClose={handleClose} />}

        </GoogleMapsProvider>
      </DialogContent>
      <DialogActions>
        {(actions || []).map(a => (
          <Button 
            key={a.label} 
            onClick={() => {
              // Execute the action
              const result = a.onClick(data);
              // Auto-close if the action returns true or nothing
              if (result !== false) {
                onClose(dialogId);
              }
            }}
          >
            {a.label}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  );
}

export default function DialogProvider({ children }) {
  const [dialogs, setDialogs] = useState([]);
  const dialogIdCounter = useRef(0);
  
  const createDialog = (options) => {
    const dialogId = ++dialogIdCounter.current;
    const dialog = { 
      ...options, 
      dialogId,
      open: true 
    };
    
    setDialogs(prevDialogs => [...prevDialogs, dialog]);
    
    // Return the dialog ID so it can be used to close specific dialogs
    return dialogId;
  };
  
  const closeDialog = (dialogId, ...data) => {
    setDialogs(prevDialogs => {
      if (typeof dialogId === 'undefined') {
        // If no ID provided, close the last dialog
        if (prevDialogs.length === 0) return prevDialogs;
        
        const lastDialog = prevDialogs[prevDialogs.length - 1];
        if (lastDialog.onClose) {
          lastDialog.onClose(...data);
        }
        
        // Mark as closed instead of removing immediately
        return prevDialogs.map((d, i) => 
          i === prevDialogs.length - 1 ? { ...d, open: false } : d
        );
      } else {
        // Close specific dialog by ID
        return prevDialogs.map(dialog => {
          if (dialog.dialogId === dialogId) {
            if (dialog.onClose && dialog.open) {
              dialog.onClose(...data);
            }
            return { ...dialog, open: false };
          }
          return dialog;
        });
      }
    });
  };
  
  const removeDialog = (dialogId) => {
    setDialogs(prevDialogs => 
      prevDialogs.filter(d => d.dialogId !== dialogId)
    );
  };
  
  const contextValue = useRef([createDialog, closeDialog]);

  useEffect(() => {
    contextValue.current = [createDialog, closeDialog];
  }, []);
  
  return (
    <DialogContext.Provider value={contextValue.current}>
      {children}
      {dialogs.map((dialog) => {
        const { onClose: onDialogClose, ...dialogParams } = dialog;

        return (
          <DialogContainer
            key={dialog.dialogId}
            dialogId={dialog.dialogId}
            onClose={closeDialog}
            onKill={removeDialog}
            {...dialogParams}
          />
        );
      })}
    </DialogContext.Provider>
  );
}

// Example usage:
/*
const MyComponent = () => {
  const [openDialog, closeDialog] = useDialog();
  
  const handleReportarSiniestro = (seguro) => {
    const dialogId = openDialog({
      Content: ReportarSiniestroModal,
      seguro: seguro,
      onSubmit: async (siniestroData) => {
        try {
          await reportarSiniestro(seguro._id, siniestroData);
          queryClient.invalidateQueries(SEGURO.name);
          closeDialog(); // Will close the most recent dialog
          // OR closeDialog(dialogId) to close this specific dialog
        } catch (error) {
          console.error('Error reporting siniestro:', error);
        }
      },
      // Don't pass onClose that calls closeDialog - it's handled internally
      actions: [
        {
          label: 'Cancel',
          onClick: () => true // Returning true will auto-close
        },
        {
          label: 'Submit',
          onClick: async (data) => {
            // Do submission logic
            return true; // Auto-close on success
          }
        }
      ]
    });
  };
  
  return (
    // Your component JSX
  );
};
*/